import React, { FC, CSSProperties, useState, useEffect } from "react";
import { dropdownStyle } from '../components/StyledSelect';
import { useAuth } from "../contexts/AuthContext";
import { apiUrl } from "../utils/api";
import versionUtil from "../utils/versionUtils";

// --- Type Definitions ---

interface DropdownOption {
  value: string;
  label: string;
}

interface DropdownProps {
  options: DropdownOption[];
  value: string;
  onChange: (value: string) => void;
}

interface User {
  id: string;
  username: string;
  email: string;
  full_name: string;
  contractor_id: string;
  role: 'operator' | 'engineer' | 'admin';
  is_active: boolean;
}

// Removed unused ApiResponse

interface MenuItem {
  id: string;
  label: string;
  icon: string;
}

interface ImportItem {
  id: string;
  file_name: string;
  created_at?: string;
  source?: string;
  comment?: string;
  status?: string;
  import_type: 'drilling';
  match_suggestion?: string;
}

interface ImportListResponse {
  code?: number;
  message?: string;
  import_list?: any[];
  items?: any[];
  data?: any[];
}

// --- Components ---

const Dropdown: FC<DropdownProps> = ({ options, value, onChange }) => (
  <select 
    style={styles.dropdown} 
    value={value} 
    onChange={(e) => onChange(e.target.value)}
  >
    {options.map(option => (
      <option key={option.value} value={option.value}>
        {option.label}
      </option>
    ))}
  </select>
);

// Lightweight Modal component
const Modal: FC<{
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  contentStyle?: CSSProperties;
}> = ({ title, onClose, children, contentStyle }) => (
  <div style={styles.modalOverlay}>
    <div style={{ ...styles.modalContent, ...contentStyle }}>
      <div style={styles.modalHeader}>
        <h3 style={styles.modalTitle}>{title}</h3>
        <button aria-label="Закрыть" style={styles.modalCloseButton} onClick={onClose}>×</button>
      </div>
      {children}
    </div>
  </div>
);

// Per-row actions menu (kebab)
const RowActionMenu: FC<{ onEdit: () => void; onResetPassword: () => void; onDelete: () => void }> = ({ onEdit, onResetPassword, onDelete }) => {
  const [open, setOpen] = useState(false);
  const ref = React.useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, []);

  return (
    <div ref={ref} style={styles.kebabWrapper}>
      <button aria-label="Открыть меню действий" title="Действия" style={styles.kebabButton} onClick={() => setOpen(v => !v)}>
        ⋮
      </button>
      {open && (
        <div style={styles.rowMenu}>
          <button style={styles.rowMenuItem} onClick={() => { setOpen(false); onEdit(); }}>Редактировать пользователя</button>
          <button style={styles.rowMenuItem} onClick={() => { setOpen(false); onResetPassword(); }}>Изменить пароль</button>
          <button style={{ ...styles.rowMenuItem, color: '#ff6b6b' }} onClick={() => { setOpen(false); onDelete(); }}>Удалить пользователя</button>
        </div>
      )}
    </div>
  );
};

// Forms for Users CRUD
const UserForm: FC<{ initial?: Partial<User>; onSubmit: (payload: Partial<User> & { password?: string }) => void; contractors?: Contractor[]; createdPassword?: string; createdLogin?: string }> = ({ initial, onSubmit, contractors = [], createdPassword, createdLogin }) => {
  const [full_name, setFullName] = useState(initial?.full_name ?? '');
  const [email, setEmail] = useState(initial?.email ?? '');
  const [contractor_id, setContractorId] = useState(initial?.contractor_id ?? '');
  const [role, setRole] = useState<User['role']>(initial?.role ?? 'operator');
  const [is_active] = useState<boolean>(initial?.is_active ?? true);
  const [password, setPassword] = useState(createdPassword ?? '');
  const [login, setLogin] = useState(createdLogin ?? '');

  useEffect(() => {
    // Sync read-only credentials when parent updates after creation
    if (!initial?.id) {
      setPassword(createdPassword ?? '');
      setLogin(createdLogin ?? '');
    }
  }, [createdPassword, createdLogin, initial?.id]);

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit({ full_name, email, contractor_id, role, is_active, password: password || undefined }); }} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <label style={{ color: '#cfd3dc', fontSize: 13 }}>Компания</label>
        {initial?.id ? (
          <input
            style={styles.searchInput}
            placeholder="Компания"
            type="text"
            value={contractors.find(c => String(c.id) === String(contractor_id))?.name || ''}
            readOnly
          />
        ) : (
          <select
            style={styles.searchInput}
            value={contractor_id}
            onChange={(e) => setContractorId(e.target.value)}
          >
            {contractors.map(c => (
              <option key={c.id} value={String(c.id)}>{c.name}</option>
            ))}
          </select>
        )}

        <label style={{ color: '#cfd3dc', fontSize: 13 }}>Роль</label>
        <select
          style={styles.searchInput}
          value={role}
          onChange={(e) => setRole(e.target.value as User['role'])}
        >
          <option value="operator">Оператор</option>
          <option value="admin">Администратор</option>
          <option value="engineer">Инженер</option>
        </select>

        <label style={{ color: '#cfd3dc', fontSize: 13 }}>ФИО</label>
        <input
          style={styles.searchInput}
          placeholder="ФИО"
          type="text"
          value={full_name}
          onChange={(e) => setFullName(e.target.value)}
        />

        <label style={{ color: '#cfd3dc', fontSize: 13 }}>Email</label>
        <input
          style={styles.searchInput}
          placeholder="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        {/* Show Login/Password only during creation and only after API provides them */}
        {(!initial?.id && (login || password)) && (
          <>
            <label style={{ color: '#cfd3dc', fontSize: 13 }}>Логин</label>
            <input
              style={styles.searchInput}
              placeholder="Логин"
              type="text"
              value={login}
              readOnly
            />

            <label style={{ color: '#cfd3dc', fontSize: 13 }}>Пароль</label>
            <input
              style={styles.searchInput}
              placeholder="Пароль"
              type="text"
              value={password}
              readOnly
            />
          </>
        )}

      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        {initial?.id ? (
          <button type="submit" style={styles.addButton}>Сохранить</button>
        ) : (
          // In create mode: hide submit once credentials are shown to prevent double submit
          !(login || password) ? (
            <button type="submit" style={styles.addButton}>Добавить</button>
          ) : null
        )}
      </div>
    </form>
  );
};

const PasswordForm: FC<{ onSubmit: (password: string) => void }> = ({ onSubmit }) => {
  const [pwd, setPwd] = useState('');
  return (
    <form onSubmit={(e) => { e.preventDefault(); if (pwd) onSubmit(pwd); }} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <input style={styles.searchInput} placeholder="Новый пароль" type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} />
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button type="submit" style={styles.addButton}>Сбросить</button>
      </div>
    </form>
  );
};

// Form for Wells CRUD
const WellForm: FC<{ initial?: Partial<Well>; onSubmit: (payload: Partial<Well>) => void }> = ({ initial, onSubmit }) => {
  const [number, setNumber] = useState(initial?.number ?? '');
  const [status, setStatus] = useState(initial?.status ?? 'planned');
  const [current_depth, setCurrentDepth] = useState(initial?.current_depth ?? '');

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit({ number, status, current_depth }); }} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <label style={{ color: '#cfd3dc', fontSize: 13 }}>Название</label>
      <input style={styles.searchInput} placeholder="Название" type="text" value={number} onChange={(e) => setNumber(e.target.value)} />

      <label style={{ color: '#cfd3dc', fontSize: 13 }}>Статус</label>
      <select style={styles.searchInput} value={status} onChange={(e) => setStatus(e.target.value)}>
        <option value="planned">Планируется</option>
        <option value="drilling">Бурение</option>
        <option value="completed">Завершена</option>
        <option value="paused">Пауза</option>
        <option value="stopped">Остановлена</option>
      </select>

      <label style={{ color: '#cfd3dc', fontSize: 13 }}>Глубина</label>
      <input style={styles.searchInput} placeholder="Глубина" type="text" value={current_depth} onChange={(e) => setCurrentDepth(e.target.value)} />

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button type="submit" style={styles.addButton}>{initial?.id ? 'Сохранить' : 'Добавить'}</button>
      </div>
    </form>
  );
};

// --- Data ---

const themeOptions: DropdownOption[] = [
  { value: 'dark', label: 'Темное' },
  { value: 'light', label: 'Светлое' },
  { value: 'auto', label: 'Автоматически' }
];

const languageOptions: DropdownOption[] = [
  { value: 'ru', label: 'Русский' },
  { value: 'en', label: 'English' },
  { value: 'de', label: 'Deutsch' }
];

const timezoneOptions: DropdownOption[] = [
  { value: 'msk+4', label: 'МСК+4, UTC+7' },
  { value: 'msk', label: 'МСК, UTC+3' },
  { value: 'utc', label: 'UTC+0' }
];

const listOptions: DropdownOption[] = [
  { value: 'list', label: 'Список' },
  { value: 'grid', label: 'Сетка' },
  { value: 'table', label: 'Таблица' }
];

// --- Menu Items ---

const menuItems: MenuItem[] = [
  { id: 'users', label: 'Пользователи', icon: '👥' },
  { id: 'admins', label: 'Администраторы', icon: '🔧' },
  { id: 'companies', label: 'Компании', icon: '🏢' },
  { id: 'drilling', label: 'Буровая', icon: '⚙️' },
  { id: 'wells', label: 'Скважины', icon: '🕳️' },
  { id: 'imports', label: 'Импорт данных', icon: '📁' },
  { id: 'settings', label: 'Настройки', icon: '⚙️' }
];

// --- API Functions ---

// --- Admin API clients ---
const authHeader = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
});

const DRILLING_IMPORTS_PATH = '/drilling/data/imports/';

// Users
const fetchUsers = async (): Promise<User[]> => {
  try {
    const response = await fetch(apiUrl('/users/'), { method: 'GET', headers: authHeader() });
    if (!response.ok) throw new Error('Failed to fetch users');
    const data = await response.json();
    const list = Array.isArray(data?.user_list) ? data.user_list : [];
    // Normalize API fields to User type shape (email/is_active may be absent in API)
    return list.map((u: any) => ({
      id: u.id,
      username: u.login ?? '',
      email: u.email ?? '',
      full_name: u.full_name ?? '',
      contractor_id: u.contractor_id ?? '',
      role: (u.role ?? 'operator') as User['role'],
      is_active: u.is_active ?? true,
    }));
  } catch (error) {
    console.error('[AdminAPI] Error fetching users:', error);
    return [];
  }
};

const createUser = async (payload: Partial<User>): Promise<any | null> => {
  try {
    // API expects: full_name, role, contractor_id
    const body = {
      full_name: payload.full_name,
      role: payload.role,
      contractor_id: payload.contractor_id,
    };
    const response = await fetch(apiUrl('/users/'), { method: 'POST', headers: authHeader(), body: JSON.stringify(body) });
    if (!response.ok) throw new Error('Failed to create user');
    return await response.json();
  } catch (error) {
    console.error('[AdminAPI] Error creating user:', error);
    return null;
  }
};

const updateUser = async (userId: string, payload: Partial<User>): Promise<User | null> => {
  try {
    const response = await fetch(apiUrl(`/users/${userId}`), { method: 'PATCH', headers: authHeader(), body: JSON.stringify(payload) });
    if (!response.ok) throw new Error('Failed to update user');
    return await response.json();
  } catch (error) {
    console.error('[AdminAPI] Error updating user:', error);
    return null;
  }
};

const deleteUser = async (userId: string): Promise<boolean> => {
  try {
    const response = await fetch(apiUrl(`/users/${userId}`), { method: 'DELETE', headers: authHeader() });
    if (!response.ok) throw new Error('Failed to delete user');
    return true;
  } catch (error) {
    console.error('[AdminAPI] Error deleting user:', error);
    return false;
  }
};

const resetUserPassword = async (userId: string, newPassword: string): Promise<boolean> => {
  try {
    const response = await fetch(apiUrl(`/users/${userId}/password`), { method: 'POST', headers: authHeader(), body: JSON.stringify({ password: newPassword }) });
    if (!response.ok) throw new Error('Failed to reset password');
    return true;
  } catch (error) {
    console.error('[AdminAPI] Error resetting user password:', error);
    return false;
  }
};

// Generic helpers for other entities
interface Contractor { id: string; name: string; id_int?: string; }
interface Station { id: string; name: string; mine_id?: string; }
// Removed unused Mine interface
interface Location { id: string; name: string; mine_id?: string; }
interface Well { id: string; number: string; location_id?: string; station_id?: string; status?: string; current_depth?: string; }
// --- Localization helpers ---
const translateStatus = (status?: string): string => {
  switch ((status || '').toLowerCase()) {
    case 'planned': return 'Планируется';
    case 'drilling': return 'Бурение';
    case 'completed': return 'Завершена';
    case 'paused': return 'Пауза';
    case 'stopped': return 'Остановлена';
    default: return status || '';
  }
};

// Removed unused generic fetchList helper

// Specific: Contractors list using contractor_list
const fetchContractors = async (): Promise<Contractor[]> => {
  try {
    const r = await fetch(apiUrl('/contractors/'), { method: 'GET', headers: authHeader() });
    if (!r.ok) throw new Error('Failed to fetch /contractors/');
    const data = await r.json();
    const list = (data?.contractor_list ?? data?.items ?? data?.data ?? []);
    if (!Array.isArray(list)) return [];
    return list.map((c: any) => ({ id: c.id, name: c.name ?? c.id_int ?? '', id_int: c.id_int }));
  } catch (e) {
    console.error('[AdminAPI] Error fetching contractors', e);
    return [];
  }
};

const fetchStationsAdmin = async (): Promise<Station[]> => {
  try {
    const r = await fetch(apiUrl('/stations/'), { method: 'GET', headers: authHeader() });
    if (!r.ok) throw new Error('Failed to fetch /stations/');
    const data = await r.json();
    const list = Array.isArray(data?.station_list) ? data.station_list : [];
    return list.map((station: any) => ({
      id: station.id,
      name: station.name ?? station.id_int ?? station.id ?? '',
      mine_id: station.mine_id,
    }));
  } catch (e) {
    console.error('[AdminAPI] Error fetching stations', e);
    return [] as Station[];
  }
};

// Wells CRUD API
const fetchWellsAdmin = async (): Promise<Well[]> => {
  try {
    const r = await fetch(apiUrl('/wells/'), { method: 'GET', headers: authHeader() });
    if (!r.ok) throw new Error('Failed to fetch /wells/');
    const data = await r.json();
    const list = Array.isArray(data?.well_list) ? data.well_list : [];
    return list.map((w: any) => ({ id: w.id, number: w.name ?? '', status: w.status, current_depth: w.current_depth, location_id: w.location_id }));
  } catch (e) {
    console.error('[AdminAPI] Error fetching wells', e);
    return [] as Well[];
  }
};

const createWell = async (payload: Partial<Well>): Promise<boolean> => {
  try {
    const body: any = {
      name: payload.number,
      status: payload.status,
      current_depth: payload.current_depth,
    };
    const r = await fetch(apiUrl('/wells/'), { method: 'POST', headers: authHeader(), body: JSON.stringify(body) });
    if (!r.ok) throw new Error('Failed to create well');
    return true;
  } catch (e) {
    console.error('[AdminAPI] Error creating well', e);
    return false;
  }
};

const updateWell = async (wellId: string, payload: Partial<Well>): Promise<boolean> => {
  try {
    const body: any = {
      name: payload.number,
      status: payload.status,
      current_depth: payload.current_depth,
    };
    const r = await fetch(apiUrl(`/wells/${wellId}`), { method: 'PATCH', headers: authHeader(), body: JSON.stringify(body) });
    if (!r.ok) throw new Error('Failed to update well');
    return true;
  } catch (e) {
    console.error('[AdminAPI] Error updating well', e);
    return false;
  }
};

const deleteWell = async (wellId: string): Promise<boolean> => {
  try {
    const r = await fetch(apiUrl(`/wells/${wellId}`), { method: 'DELETE', headers: authHeader() });
    if (!r.ok) throw new Error('Failed to delete well');
    return true;
  } catch (e) {
    console.error('[AdminAPI] Error deleting well', e);
    return false;
  }
};

const fetchImportItems = async (): Promise<ImportItem[]> => {
  try {
    // First fetch all stations to query imports for each station
    const stationList = await fetchStationsAdmin();
    if (stationList.length === 0) return [];

    // Fetch imports for each station and combine results
    const allItems: ImportItem[] = [];
    
    for (const station of stationList) {
      try {
        const params = new URLSearchParams();
        params.set('station_id', station.id);
        const url = `${DRILLING_IMPORTS_PATH}?${params.toString()}`;
        
        const response = await fetch(apiUrl(url), { method: 'GET', headers: authHeader() });
        const data = response.ok ? await response.json() : {};
        const list = Array.isArray(data.import_list || data.items || data.data) ? (data.import_list || data.items || data.data) : [];

        const items = list.map((item: Record<string, any>): ImportItem => ({
          id: item.id ?? item.import_id ?? item.file_id ?? '',
          file_name: item.file_name || item.name || item.filename || '—',
          created_at: item.created_at || item.uploaded_at || item.date || '',
          source: item.source || item.station_name || station.name || '—',
          comment: item.comment || item.description || '',
          status: item.status || item.state || '—',
          import_type: 'drilling',
          match_suggestion: item.match_suggestion || item.suggestion || '',
        }));
        
        allItems.push(...items);
      } catch (e) {
        console.error(`[AdminAPI] Error fetching import items for station ${station.id}`, e);
        // Continue with next station if one fails
      }
    }

    return allItems;
  } catch (e) {
    console.error('[AdminAPI] Error fetching import items', e);
    return [];
  }
};

const uploadImportFile = async ({
  file,
  stationId,
}: {
  file: File;
  stationId?: string;
}): Promise<{ ok: boolean; message?: string }> => {
  try {
    if (!stationId) throw new Error('Выберите буровую');

    const form = new FormData();
    form.append('file', file);

    const params = new URLSearchParams();
    params.set('station_id', stationId);

    const uploadUrl = `${DRILLING_IMPORTS_PATH}?${params.toString()}`;
    console.log('[AdminAPI] Upload telemetry import:', uploadUrl);

    const response = await fetch(apiUrl(uploadUrl), {
      method: 'POST',
      headers: { Authorization: authHeader().Authorization },
      body: form,
    });

    const data = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(data?.message || `Ошибка импорта файла: ${response.status}`);
    }

    if (data?.code !== undefined && data.code !== 0) {
      throw new Error(data?.message || 'API импорта вернул ошибку');
    }

    return { ok: true };
  } catch (e) {
    const message = e instanceof Error ? e.message : 'Не удалось импортировать файл';
    console.error('[AdminAPI] Error uploading telemetry file', e);
    return { ok: false, message };
  }
};

const retryImportItem = async (item: ImportItem): Promise<boolean> => {
  try {
    const r = await fetch(apiUrl(`${DRILLING_IMPORTS_PATH}/${item.id}/retry`), { method: 'POST', headers: authHeader() });
    if (!r.ok) throw new Error(`Failed to retry import ${r.status}`);
    return true;
  } catch (e) {
    console.error('[AdminAPI] Error retrying import item', e);
    return false;
  }
};

// Removed unused generic createItem helper

// Removed unused generic updateItem helper

// Removed unused generic deleteItem helper

// --- Mock Data ---

// Removed unused mockAdmins

// Removed unused mockLocations

// Removed unused mockWells

// --- Section Components ---

interface SectionProps {
    title: string;
}

const UsersSection: FC<SectionProps> = ({ title }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [createdPassword, setCreatedPassword] = useState<string>('');
  const [createdLogin, setCreatedLogin] = useState<string>('');
  const [editUser, setEditUser] = useState<User | null>(null);
  const [resetUser, setResetUser] = useState<User | null>(null);
  const [contractors, setContractors] = useState<Contractor[]>([]);

  const contractorNameById = (id?: string | null) => {
    if (!id) return '';
    const found = contractors.find(c => c.id === id);
    return found?.name ?? id;
  };

  const loadUsers = async () => {
    setLoading(true);
    const fetchedUsers = await fetchUsers();
    setUsers(fetchedUsers);
    setLoading(false);
  };

  useEffect(() => { loadUsers(); }, []);
  useEffect(() => { (async () => { const list = await fetchContractors(); setContractors(list); })(); }, []);

  const filteredUsers = (Array.isArray(users) ? users : []).filter(user => 
    (user.full_name ?? '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.email ?? '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin': return 'Администратор';
      case 'engineer': return 'Инженер';
      case 'operator': return 'Оператор';
      default: return role;
    }
  };

  return (
    <div style={styles.sectionContent}>
      <div style={styles.sectionHeader}>
        <h2 style={styles.sectionTitle}>{title}</h2>
        <div style={styles.headerActions}>
          <input 
            type="text"
            placeholder="Поиск"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
          <button style={styles.addButton} onClick={() => setShowCreate(true)}>Добавить пользователя</button>
        </div>
      </div>

      {loading ? (
        <div style={styles.loading}>Загрузка...</div>
      ) : (
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.tableHeader}>
                <th style={styles.th}></th>
                <th style={styles.th}>ФИО</th>
                <th style={styles.th}>Логин</th>
                <th style={styles.th}>Компания</th>
                <th style={styles.th}>ID</th>
                <th style={styles.th}>Роль</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>
                  
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.id} style={styles.tableRow}>
                  <td style={styles.td}></td>
                  <td style={styles.td}>{user.full_name}</td>
                  <td style={styles.td}>{user.username}</td>
                  <td style={styles.td}>{contractorNameById(user.contractor_id)}</td>
                  <td style={styles.td}>{user.id?.substring(0, 8)}</td>
                  <td style={styles.td}>{getRoleLabel(user.role)}</td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>
                    <RowActionMenu
                      onEdit={() => setEditUser(user)}
                      onResetPassword={() => setResetUser(user)}
                      onDelete={async () => { if (await deleteUser(user.id)) loadUsers(); }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {showCreate && (
        <Modal onClose={() => { setShowCreate(false); setCreatedPassword(''); setCreatedLogin(''); }} title="Добавить пользователя">
            <UserForm contractors={contractors} createdPassword={createdPassword} createdLogin={createdLogin} onSubmit={async (payload) => {
              const created = await createUser(payload);
              if (created) {
                setCreatedPassword(created.password ?? '');
                setCreatedLogin(created.login ?? '');
                setShowCreate(true);
              }
              loadUsers();
            }} />
        </Modal>
      )}

      {/* Edit Modal */}
      {editUser && (
        <Modal onClose={() => setEditUser(null)} title="Изменить пользователя">
          <UserForm initial={editUser} contractors={contractors} onSubmit={async (payload) => { const updated = await updateUser(editUser.id, payload); if (updated) { setEditUser(null); loadUsers(); } }} />
        </Modal>
      )}

      {/* Reset Password Modal */}
      {resetUser && (
        <Modal onClose={() => setResetUser(null)} title={`Сброс пароля: ${resetUser.full_name}`}>
          <PasswordForm onSubmit={async (pwd) => { const ok = await resetUserPassword(resetUser.id, pwd); if (ok) setResetUser(null); }} />
        </Modal>
      )}
    </div>
  );
};

const AdminsSection: FC<SectionProps> = ({ title }) => {
  const [admins, setAdmins] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [contractors, setContractors] = useState<Contractor[]>([]);

  const contractorNameById = (id?: string | null) => {
    if (!id) return '';
    const found = contractors.find(c => c.id === id);
    return found?.name ?? id;
  };

  useEffect(() => { (async () => { const list = await fetchContractors(); setContractors(list); })(); }, []);

  const loadAdmins = async () => {
    setLoading(true);
    const fetchedUsers = await fetchUsers();
    const onlyAdmins = fetchedUsers.filter(u => u.role === 'admin');
    setAdmins(onlyAdmins);
    setLoading(false);
  };

  useEffect(() => { loadAdmins(); }, []);

  const filteredAdmins = admins.filter(a =>
    (a.full_name ?? '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (a.username ?? '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div style={styles.sectionContent}>
      <div style={styles.sectionHeader}>
        <h2 style={styles.sectionTitle}>{title}</h2>
        <div style={styles.headerActions}>
          <input 
            type="text"
            placeholder="Поиск"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
        </div>
      </div>
      {loading ? (
        <div style={styles.loading}>Загрузка...</div>
      ) : (
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.tableHeader}>
                <th style={styles.th}></th>
                <th style={styles.th}>ФИО</th>
                <th style={styles.th}>Логин</th>
                <th style={styles.th}>Компания</th>
                <th style={styles.th}>ID</th>
              </tr>
            </thead>
            <tbody>
              {filteredAdmins.map((admin) => (
                <tr key={admin.id} style={styles.tableRow}>
                  <td style={styles.td}></td>
                  <td style={styles.td}>{admin.full_name}</td>
                  <td style={styles.td}>{admin.username}</td>
                  <td style={styles.td}>{contractorNameById(admin.contractor_id)}</td>
                  <td style={styles.td}>{admin.id?.substring(0,8)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const CompaniesSection: FC<SectionProps> = ({ title }) => {
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [stations, setStations] = useState<Station[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const loadData = async () => {
    setLoading(true);
    const [ctrs, usrs, stns] = await Promise.all([
      fetchContractors(),
      fetchUsers(),
      (async () => {
        try {
          const r = await fetch(apiUrl('/stations/'), { method: 'GET', headers: authHeader() });
          if (!r.ok) throw new Error('Failed to fetch /stations/');
          const data = await r.json();
          const list = Array.isArray(data?.station_list) ? data.station_list : [];
          return list.map((s: any) => ({ id: s.id, name: s.name ?? '', mine_id: s.mine_id, contractor_id: s.contractor_id }));
        } catch (e) {
          console.error('[AdminAPI] Error fetching stations', e);
          return [] as Station[];
        }
      })()
    ]);
    setContractors(ctrs);
    setUsers(usrs);
    setStations(stns);
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  const usersCountByContractor = (contractorId: string) => users.filter(u => String(u.contractor_id) === String(contractorId)).length;
  const stationsCountByContractor = (contractorId: string) => stations.filter(s => String((s as any).contractor_id) === String(contractorId)).length;

  const filteredContractors = contractors.filter(c => (c.name ?? '').toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div style={styles.sectionContent}>
      <div style={styles.sectionHeader}>
        <h2 style={styles.sectionTitle}>{title}</h2>
        <div style={styles.headerActions}>
          <input 
            type="text"
            placeholder="Поиск компаний"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
        </div>
      </div>
      {loading ? (
        <div style={styles.loading}>Загрузка...</div>
      ) : (
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.tableHeader}>
                <th style={styles.th}></th>
                <th style={styles.th}>Компания</th>
                <th style={styles.th}>Группа ID</th>
                <th style={styles.th}>Пользователей</th>
                <th style={styles.th}>Буровых (станков)</th>
              </tr>
            </thead>
            <tbody>
              {filteredContractors.map((c) => (
                <tr key={c.id} style={styles.tableRow}>
                  <td style={styles.td}></td>
                  <td style={styles.td}>{c.name}</td>
                  <td style={styles.td}>{(c as any).id_int ?? ''}</td>
                  <td style={styles.td}>{usersCountByContractor(c.id)}</td>
                  <td style={styles.td}>{stationsCountByContractor(c.id)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const WellsSection: FC<SectionProps> = ({ title }) => {
  const [wells, setWells] = useState<Well[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editWell, setEditWell] = useState<Well | null>(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const wls = await fetchWellsAdmin();
      setWells(wls);
    } catch (e) {
      console.error('[AdminAPI] Error loading wells data', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const filteredWells = wells.filter(w => (w.number ?? '').toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div style={styles.sectionContent}>
      <div style={styles.sectionHeader}>
        <h2 style={styles.sectionTitle}>{title}</h2>
        <div style={styles.headerActions}>
          <input 
            type="text"
            placeholder="Поиск скважин"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
          <button style={styles.addButton} onClick={() => setShowCreate(true)}>Добавить скважину</button>
        </div>
      </div>
      {loading ? (
        <div style={styles.loading}>Загрузка...</div>
      ) : (
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.tableHeader}>
                <th style={styles.th}></th>
                <th style={styles.th}>Название</th>
                <th style={styles.th}>Статус</th>
                <th style={styles.th}>Глубина</th>
                <th style={{ ...styles.th, textAlign: 'right' }}></th>
              </tr>
            </thead>
            <tbody>
              {filteredWells.map((w) => (
                <tr key={w.id} style={styles.tableRow}>
                  <td style={styles.td}></td>
                  <td style={styles.td}>{w.number}</td>
                  <td style={styles.td}>{translateStatus(w.status)}</td>
                  <td style={styles.td}>{w.current_depth}</td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>
                    <RowActionMenu
                      onEdit={() => setEditWell(w)}
                      onResetPassword={() => {/* not applicable for wells */}}
                      onDelete={async () => { if (await deleteWell(w.id)) loadData(); }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Well Modal */}
      {showCreate && (
        <Modal onClose={() => setShowCreate(false)} title="Добавить скважину">
          <WellForm onSubmit={async (payload) => { const ok = await createWell(payload); if (ok) { setShowCreate(false); loadData(); } }} />
        </Modal>
      )}

      {/* Edit Well Modal */}
      {editWell && (
        <Modal onClose={() => setEditWell(null)} title="Изменить скважину">
          <WellForm initial={editWell} onSubmit={async (payload) => { const ok = await updateWell(editWell.id, payload); if (ok) { setEditWell(null); loadData(); } }} />
        </Modal>
      )}
    </div>
  );
};

const ImportSection: FC<SectionProps> = ({ title }) => {
  const [items, setItems] = useState<ImportItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [stations, setStations] = useState<Station[]>([]);
  const [selectedStationId, setSelectedStationId] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [retryItem, setRetryItem] = useState<ImportItem | null>(null);

  const loadData = async () => {
    setLoading(true);
    const list = await fetchImportItems();
    setItems(list);
    setLoading(false);
  };

  const loadImportDictionaries = async () => {
    const stationList = await fetchStationsAdmin();
    setStations(stationList);
    setSelectedStationId(current => current || stationList[0]?.id || '');
  };

  useEffect(() => { loadData(); }, []);
  useEffect(() => { loadImportDictionaries(); }, []);

  const resetUploadForm = () => {
    setFile(null);
    setUploadError('');
    setUploading(false);
    setSelectedStationId(stations[0]?.id || '');
  };

  const openUploadModal = () => {
    resetUploadForm();
    setShowUpload(true);
  };

  const closeUploadModal = () => {
    setShowUpload(false);
    resetUploadForm();
  };

  const handleUpload = async () => {
    if (uploading) return;

    if (!file) {
      setUploadError('Выберите CSV-файл для импорта.');
      return;
    }

    if (!selectedStationId) {
      setUploadError('Выберите буровую.');
      return;
    }

    setUploading(true);
    setUploadError('');

    const result = await uploadImportFile({
      file,
      stationId: selectedStationId,
    });

    setUploading(false);

    if (result.ok) {
      closeUploadModal();
      loadData();
      return;
    }

    setUploadError(result.message || 'Не удалось импортировать файл. Проверьте выбранную буровую и формат CSV.');
  };

  const handleRetry = async (item: ImportItem) => {
    const success = await retryImportItem(item);
    if (success) loadData();
  };

  const handleFileDrop = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    const droppedFile = event.dataTransfer.files?.[0];
    if (droppedFile) {
      setFile(droppedFile);
      setUploadError('');
    }
  };

  const formatDate = (value?: string) => {
    if (!value) return '—';
    const date = new Date(value);
    return Number.isFinite(date.getTime()) ? date.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : value;
  };

  const filteredItems = items.filter(item =>
    item.file_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(item.source || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(item.comment || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(item.status || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const isUploadDisabled = uploading;

  return (
    <div style={styles.sectionContent}>
      <div style={styles.sectionHeader}>
        <h2 style={styles.sectionTitle}>{title}</h2>
        <div style={styles.headerActions}>
          <input
            type="text"
            placeholder="Поиск"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
          <button style={styles.addButton} onClick={openUploadModal}>Загрузить файл</button>
        </div>
      </div>

      {loading ? (
        <div style={styles.loading}>Загрузка...</div>
      ) : (
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.tableHeader}>
                <th style={styles.th}></th>
                <th style={styles.th}>Имя файла</th>
                <th style={styles.th}>Дата / время</th>
                <th style={styles.th}>Источник</th>
                <th style={styles.th}>Комментарий</th>
                <th style={styles.th}>Статус</th>
                <th style={styles.th}>Тип</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item) => (
                <tr key={item.id} style={styles.tableRow}>
                  <td style={styles.td}></td>
                  <td style={styles.td}>{item.file_name}</td>
                  <td style={styles.td}>{formatDate(item.created_at)}</td>
                  <td style={styles.td}>{item.source || '—'}</td>
                  <td style={styles.td}>{item.comment || '—'}</td>
                  <td style={styles.td}>{item.status || '—'}</td>
                  <td style={styles.td}>Телеметрия</td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>
                    <button style={styles.smallActionButton} onClick={() => setRetryItem(item)}>{item.status?.toLowerCase().includes('ошибка') || item.status?.toLowerCase().includes('error') ? 'Повторить' : 'Просмотр'}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showUpload && (
        <Modal
          title="Импорт данных"
          onClose={closeUploadModal}
          contentStyle={styles.importModal}
        >
          <div style={styles.importModalBody}>
            <label
              style={styles.importDropzone}
              onDragOver={(event) => event.preventDefault()}
              onDrop={handleFileDrop}
            >
              <span style={styles.importDropzoneIcon} />
              <span style={styles.importDropzoneText}>{file ? file.name : 'Загрузите файл'}</span>
              <input
                type="file"
                accept=".csv,text/csv"
                style={{ display: 'none' }}
                onChange={(event) => {
                  setFile(event.target.files ? event.target.files[0] : null);
                  setUploadError('');
                }}
              />
            </label>

            <div style={styles.importFormGrid}>
              <div style={styles.importRow}>
                <label style={styles.importLabel}>Буровая</label>
                <select
                  style={styles.importInput}
                  value={selectedStationId}
                  onChange={(event) => {
                    setSelectedStationId(event.target.value);
                    setUploadError('');
                  }}
                >
                  {stations.length ? (
                    stations.map((station) => (
                      <option key={station.id} value={station.id}>{station.name || station.id}</option>
                    ))
                  ) : (
                    <option value="">Буровые не найдены</option>
                  )}
                </select>
              </div>
            </div>

            {uploadError && <div style={styles.importError}>{uploadError}</div>}

            <div style={styles.importFooter}>
              <button
                type="button"
                style={styles.importCancelButton}
                onClick={closeUploadModal}
              >
                Отмена
              </button>
              <button
                type="button"
                style={{ ...styles.importSubmitButton, ...(isUploadDisabled ? styles.importSubmitButtonDisabled : {}) }}
                disabled={isUploadDisabled}
                onClick={handleUpload}
              >
                {uploading ? 'Импортируется...' : 'Импортировать'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {retryItem && (
        <Modal title="Детали импорта" onClose={() => setRetryItem(null)}>
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={styles.modalInfoRow}><strong>Файл:</strong> {retryItem.file_name}</div>
            <div style={styles.modalInfoRow}><strong>Тип:</strong> Телеметрия</div>
            <div style={styles.modalInfoRow}><strong>Статус:</strong> {retryItem.status}</div>
            <div style={styles.modalInfoRow}><strong>Источник:</strong> {retryItem.source || '—'}</div>
            <div style={styles.modalInfoRow}><strong>Комментарий:</strong> {retryItem.comment || '—'}</div>
            {retryItem.match_suggestion && (
              <div style={styles.matchSuggestion}>
                <div style={{ marginBottom: 6, color: '#a0aec0' }}>Найдено совпадение:</div>
                <div>{retryItem.match_suggestion}</div>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button style={styles.actionButton} onClick={() => setRetryItem(null)}>Закрыть</button>
              <button style={styles.addButton} onClick={async () => { await handleRetry(retryItem!); setRetryItem(null); }}>Повторить</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

const DrillingSection: FC<SectionProps> = ({ title }) => {
  const [stations, setStations] = useState<Station[]>([]);
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [wells, setWells] = useState<Well[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const contractorNameById = (id?: string | null) => {
    if (!id) return '';
    const found = contractors.find(c => c.id === id);
    return found?.name ?? id;
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [cts, stns, wls] = await Promise.all([
        fetchContractors(),
        (async () => {
          const r = await fetch(apiUrl('/stations/'), { method: 'GET', headers: authHeader() });
          if (!r.ok) throw new Error('Failed to fetch /stations/');
          const data = await r.json();
          const list = Array.isArray(data?.station_list) ? data.station_list : [];
          return list.map((s: any) => ({ id: s.id, name: s.name ?? '', mine_id: s.mine_id, contractor_id: s.contractor_id, status: s.status, well_id: s.well_id }));
        })(),
        (async () => {
          // Fetch wells; API returns well_list with id, name, status, current_depth, location_id
          try {
            const r = await fetch(apiUrl('/wells/'), { method: 'GET', headers: authHeader() });
            if (!r.ok) throw new Error('Failed to fetch /wells/');
            const data = await r.json();
            const list = Array.isArray(data?.well_list) ? data.well_list : [];
            return list.map((w: any) => ({ id: w.id, number: w.name ?? '', location_id: w.location_id }));
          } catch (e) {
            console.error('[AdminAPI] Error fetching wells', e);
            return [] as Well[];
          }
        })(),
      ]);
      setContractors(cts);
      setStations(stns);
      setWells(wls);
    } catch (e) {
      console.error('[AdminAPI] Error loading drilling data', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  // Without direct station_id in well_list, approximate by matching station.well_id (current well) occurrences
  const wellsCountForStation = (stationId: string) => {
    const station = stations.find(s => String(s.id) === String(stationId));
    if (!station) return 0;
    const currentWellId = (station as any).well_id;
    if (!currentWellId) return 0;
    return wells.filter(w => String(w.id) === String(currentWellId)).length;
  };

  const filteredStations = stations.filter(s => (s.name ?? '').toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div style={styles.sectionContent}>
      <div style={styles.sectionHeader}>
        <h2 style={styles.sectionTitle}>{title}</h2>
        <div style={styles.headerActions}>
          <input 
            type="text"
            placeholder="Поиск буровых"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
        </div>
      </div>
      {loading ? (
        <div style={styles.loading}>Загрузка...</div>
      ) : (
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.tableHeader}>
                <th style={styles.th}></th>
                <th style={styles.th}>Имя</th>
                <th style={styles.th}>Компания</th>
                <th style={styles.th}>ID</th>
                <th style={styles.th}>Количество скважин</th>
              </tr>
            </thead>
            <tbody>
              {filteredStations.map((s) => (
                <tr key={s.id} style={styles.tableRow}>
                  <td style={styles.td}></td>
                  <td style={styles.td}>{s.name}</td>
                  <td style={styles.td}>{contractorNameById((s as any).contractor_id)}</td>
                  <td style={styles.td}>{String(s.id).substring(0,8)}</td>
                  <td style={styles.td}>{wellsCountForStation(s.id)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const LocationsSection: FC<SectionProps> = ({ title }) => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [wells, setWells] = useState<Well[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const loadData = async () => {
    setLoading(true);
    try {
      const [locs, wls] = await Promise.all([
        (async () => {
          const r = await fetch(apiUrl('/locations/'), { method: 'GET', headers: authHeader() });
          if (!r.ok) throw new Error('Failed to fetch /locations/');
          const data = await r.json();
          const list = Array.isArray(data?.location_list) ? data.location_list : [];
          return list.map((l: any) => ({ id: l.id, name: l.name ?? '', mine_id: l.mine_id, id_int: l.id_int }));
        })(),
        (async () => {
          const r = await fetch(apiUrl('/wells/'), { method: 'GET', headers: authHeader() });
          if (!r.ok) throw new Error('Failed to fetch /wells/');
          const data = await r.json();
          const list = Array.isArray(data?.well_list) ? data.well_list : [];
          return list.map((w: any) => ({ id: w.id, number: w.name ?? '', location_id: w.location_id }));
        })(),
      ]);
      setLocations(locs);
      setWells(wls);
    } catch (e) {
      console.error('[AdminAPI] Error loading locations data', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const wellsCountForLocation = (locationId: string) => wells.filter(w => String(w.location_id) === String(locationId)).length;

  const filteredLocations = locations.filter(l => (l.name ?? '').toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div style={styles.sectionContent}>
      <div style={styles.sectionHeader}>
        <h2 style={styles.sectionTitle}>{title}</h2>
        <div style={styles.headerActions}>
          <input 
            type="text"
            placeholder="Поиск месторождений"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
        </div>
      </div>
      {loading ? (
        <div style={styles.loading}>Загрузка...</div>
      ) : (
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.tableHeader}>
                <th style={styles.th}></th>
                <th style={styles.th}>Название</th>
                <th style={styles.th}>Группа ID</th>
                <th style={styles.th}>ID</th>
                <th style={styles.th}>Скважин</th>
              </tr>
            </thead>
            <tbody>
              {filteredLocations.map((l) => (
                <tr key={l.id} style={styles.tableRow}>
                  <td style={styles.td}></td>
                  <td style={styles.td}>{l.name}</td>
                  <td style={styles.td}>{(l as any).id_int ?? ''}</td>
                  <td style={styles.td}>{String(l.id).substring(0,8)}</td>
                  <td style={styles.td}>{wellsCountForLocation(l.id)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const SettingsSection: FC<SectionProps> = ({ title }) => {
  const [theme, setTheme] = useState('dark');
  const [language, setLanguage] = useState('ru');
  const [timezone, setTimezone] = useState('msk+4');
  const [listView, setListView] = useState('list');
  const { user, logout } = useAuth();

  useEffect(() => {
    if (user) {
      console.log(`[SettingsSection] Открыт раздел настроек. Пользователь: ${user.full_name} (${user.role})`);
    }
  }, [user]);

  return (
    <div style={styles.sectionContent}>
      <div style={styles.topSection}>
        <div style={styles.settingsCard}>
          <div style={styles.settingsGrid}>
            <div style={styles.settingItem}>
              <div style={styles.settingLabel}>Изменить оформление:</div>
              <Dropdown options={themeOptions} value={theme} onChange={setTheme} />
            </div>
            <div style={styles.settingItem}>
              <div style={styles.settingLabel}>Язык/Language:</div>
              <Dropdown options={languageOptions} value={language} onChange={setLanguage} />
            </div>
            <div style={styles.settingItem}>
              <div style={styles.settingLabel}>Часовой пояс:</div>
              <Dropdown options={timezoneOptions} value={timezone} onChange={setTimezone} />
            </div>
            <div style={styles.settingItem}>
              <div style={styles.settingLabel}>Основной текст:</div>
              <Dropdown options={listOptions} value={listView} onChange={setListView} />
            </div>
          </div>
        </div>
        <div style={styles.userInfo}>
          <div style={styles.avatar}></div>
          <div style={styles.userDetails}>
            <div style={styles.userName}>{user?.full_name}</div>
            <div style={styles.userEmail}>{user?.email}</div>
          </div>
          <div style={styles.userActions}>
            <button style={styles.changePasswordBtn}>Изменить пароль</button>
            <button style={styles.logoutBtn} onClick={logout}>Выйти из аккаунта</button>
          </div>
        </div>
      </div>
      <div style={styles.contentSection}>
        <h2 style={{...styles.sectionTitle, fontSize: '18px', fontWeight: '600', color: '#fff', margin: 0, marginBottom: '20px'}}>Программно-аппаратный комплекс DART ver {versionUtil.getVersion(3)}</h2>
        <div style={styles.description}>
          <p style={styles.descriptionParagraph}>
            ПАК «DART» разработан для обеспечения системы диспетчеризации шахтного бурения и предназначен 
            для установки на ПК и мобильные устройства сотрудников отделов инженерного контроля бурения.
          </p>
          <p style={styles.descriptionParagraph}>
            В ПО комплекса реализована визуализация оперативной информации с буровых станков по критическим параметрам 
            в режиме реального времени. Информация выводится во вкладках по каждому эксплуатируемому буровому станку.
          </p>
          <p style={styles.descriptionParagraph}>
            Больше информации на официальном сайте: <a href="https://geodart.ru" style={styles.link}>https://geodart.ru</a>
          </p>
        </div>
        <div style={styles.companyInfo}>
          <p style={styles.companyLine}>© ООО "ГеоДАРТ"</p>
          <p style={styles.companyLine}>ИНН: 4205455895</p>
          <p style={styles.companyLine}>ОГРН: 1234200001406</p>
          <p style={styles.companyLine}>КПП: 420501001</p>
        </div>
        <div style={styles.footer}>
          <div style={styles.disclaimer}>
            Деятельность осуществляется при грантовой поддержке Фонда «Сколково»
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Main Component ---

const getSectionTitle = (section: string): string => {
  switch (section) {
    case 'users': return 'Список пользователей';
    case 'admins': return 'Список администраторов';
    case 'companies': return 'Список компаний';
    case 'drilling': return 'Список буровых';
    case 'wells': return 'Список скважин';
    case 'imports': return 'Импорт данных';
    case 'settings': return 'Настройки';
    default: return 'Список пользователей';
  }
};

const SettingsPage: FC = () => {
  const [activeSection, setActiveSection] = useState('users');
  const { user } = useAuth();
  
  // Filter menu items based on user role
  const getAvailableMenuItems = () => {
    if (user?.role === 'admin') {
      return menuItems; // Show all menu items for admin
    } else {
      // Show only Settings section for non-admin users
      return menuItems.filter(item => item.id === 'settings');
    }
  };
  
  // Set default active section based on user role
  useEffect(() => {
    if (user) {
      console.log(`[SettingsPage] Пользователь загрузил страницу настроек. Роль: ${user.role}, ФИО: ${user.full_name}, Email: ${user.email}`);
      
      if (user.role !== 'admin') {
        console.log(`[SettingsPage] Неадминистратор автоматически перенаправлен в раздел "Настройки"`);
        setActiveSection('settings');
      } else {
        console.log(`[SettingsPage] Администратор имеет доступ ко всем разделам`);
      }
    }
  }, [user]);

  const renderSection = () => {
    const sectionTitle = getSectionTitle(activeSection);

    // Non-admin users can only access settings
    if (user?.role !== 'admin' && activeSection !== 'settings') {
      console.log(`[SettingsPage] ДОСТУП ОГРАНИЧЕН: Пользователь с ролью "${user?.role}" попытался получить доступ к разделу "${activeSection}". Перенаправлен в настройки.`);
      return <SettingsSection title="Настройки" />;
    }
    
    switch (activeSection) {
      case 'users': return <UsersSection title={sectionTitle} />;
      case 'admins': return <AdminsSection title={sectionTitle} />;
      case 'companies': return <CompaniesSection title={sectionTitle} />;
      case 'drilling': return <DrillingSection title={sectionTitle} />;
      case 'wells': return <WellsSection title={sectionTitle} />;
      case 'imports': return <ImportSection title={sectionTitle} />;
      case 'settings': return <SettingsSection title={sectionTitle} />;
      default: return <SettingsSection title="Настройки" />;
    }
  };

  const availableMenuItems = getAvailableMenuItems();

  return (
    <div style={styles.container}>
      {/* Sidebar Menu */}
      <div style={styles.sidebar}>
        {availableMenuItems.map((item) => (
          <div
            key={item.id}
            style={{
              ...styles.menuItem,
              background: activeSection === item.id ? '#2E3749' : 'transparent'
            }}
            onClick={() => {
              console.log(`[SettingsPage] Переход в раздел: "${item.label}" (${item.id}). Роль пользователя: ${user?.role}`);
              setActiveSection(item.id);
            }}
          >
            <span style={styles.menuIcon}>{item.icon}</span>
            <span style={styles.menuLabel}>{item.label}</span>
          </div>
        ))}
      </div>

      {/* Main Content */}
      <div style={styles.mainContent}>
        {renderSection()}
      </div>
    </div>
  );
};

export default SettingsPage;

// --- Styles ---

const styles: { [key: string]: CSSProperties } = {
  container: {
    display: 'flex',
    height: '100vh',
    backgroundColor: '#1A2030',
    fontFamily: 'IBM Plex Sans, "Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
  },

  modalOverlay: {
    position: 'fixed',
    inset: 0 as any,
    background: 'rgba(6, 10, 18, 0.58)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },

  modalContent: {
    background: '#1F2534',
    border: '1px solid #2E3749',
    borderRadius: '12px',
    width: '520px',
    maxWidth: '90vw',
    padding: '16px',
    color: '#fff',
    boxShadow: '0 2px 16px #0000003D',
  },

  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },

  modalTitle: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 600,
    color: '#fff',
  },

  modalCloseButton: {
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    border: 'none',
    color: '#9DA9BE',
    cursor: 'pointer',
    fontSize: '22px',
    lineHeight: 1,
    padding: 0,
  },
  
  // Sidebar styles
  sidebar: {
    width: '250px',
    backgroundColor: '#1F2534',
    padding: '20px 10px',
    borderRight: '1px solid #2E3749',
  },
  
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    marginBottom: '4px',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    color: '#fff',
  },

  menuIcon: {
    fontSize: '18px',
    marginRight: '12px',
    minWidth: '20px',
  },
  
  menuLabel: {
    fontSize: '14px',
    fontWeight: '500',
  },
  
  // Main content styles
  mainContent: {
    flex: 1,
    backgroundColor: '#1A2030',
    overflow: 'auto',
  },
  
  // Section styles
  sectionContent: {
    padding: '24px',
    color: '#fff',
  },
  
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    background: '#1F2534',
    border: '1px solid #2E3749',
    borderRadius: 12,
    padding: '12px 16px',
    boxShadow: '0 2px 16px #0000003D',
  },
  
  sectionTitle: {
    fontSize: '24px',
    fontWeight: '600',
    margin: 0,
    color: '#fff',
  },
  
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  
  searchInput: {
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid #2E3749',
    backgroundColor: '#424F67',
    color: '#fff',
    fontSize: '14px',
    width: '320px',
  },
  
  addButton: {
    padding: '8px 16px',
    backgroundColor: '#4472C4',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
  },
  
  // Table styles
  tableContainer: {
    backgroundColor: '#1F2534',
    borderRadius: '12px',
    overflow: 'hidden',
    border: '1px solid #2E3749',
    boxShadow: '0 2px 16px #0000003D',
  },
  
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    color: '#fff',
  },
  
  tableHeader: {
    backgroundColor: '#293145',
  },
  
  th: {
    padding: '12px 16px',
    textAlign: 'left' as const,
    fontWeight: '600',
    fontSize: '14px',
    borderBottom: '1px solid #444',
  },
  
  tableRow: {
    borderBottom: '1px solid #333',
    transition: 'background-color 0.15s ease-in-out',
  },
  
  td: {
    padding: '12px 16px',
    fontSize: '14px',
    verticalAlign: 'middle' as const,
  },
  
  statusBadge: {
    padding: '4px 8px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '500',
  },
  
  statusActive: {
    backgroundColor: '#22c55e',
    color: '#fff',
  },
  
  statusInactive: {
    backgroundColor: '#ef4444',
    color: '#fff',
  },
  
  actionButton: {
    padding: '4px 8px',
    backgroundColor: 'transparent',
    border: '1px solid #444',
    borderRadius: '4px',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '12px',
    marginRight: '8px',
  },
  
  // Row actions (kebab) styles
  kebabWrapper: {
    position: 'relative',
    display: 'inline-block',
  },
  kebabButton: {
    background: 'transparent',
    border: '1px solid #2E3749',
    color: '#cfd3dc',
    borderRadius: '8px',
    padding: '6px 10px',
    fontSize: '16px',
    lineHeight: 1,
    cursor: 'pointer',
  },
  rowMenu: {
    position: 'absolute',
    right: 0,
    top: '110%',
    background: '#1F2534',
    border: '1px solid #2E3749',
    borderRadius: '10px',
    boxShadow: '0 2px 16px #0000003D',
    minWidth: '220px',
    zIndex: 10,
    padding: '8px',
  },
  rowMenuItem: {
    width: '100%',
    textAlign: 'left',
    background: 'transparent',
    border: 'none',
    color: '#fff',
    fontSize: '13px',
    padding: '8px 10px',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  
  // Settings section specific styles
  topSection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'stretch',
    gap: '20px',
    width: '100%',
    marginBottom: '30px',
  },
  
  settingsCard: {
    background: '#1F2534',
    borderRadius: '12px',
    padding: '16px',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    border: '1px solid #2E3749',
    boxShadow: '0 2px 16px #0000003D',
  },
  
  settingsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows: '1fr 1fr',
    gap: '12px',
  },
  
  settingItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  
  settingLabel: {
    fontSize: '13px',
    color: '#ccc',
    fontWeight: '500',
    marginBottom: '2px',
  },
  
  dropdown: dropdownStyle,
  
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    padding: '16px',
    background: '#1F2534',
    borderRadius: '12px',
    flex: '0 0 550px',
    border: '1px solid #2E3749',
    boxShadow: '0 2px 16px #0000003D',
  },
  
  avatar: {
    width: '50px',
    height: '50px',
    borderRadius: '50%',
    background: '#555',
  },
  
  userDetails: {
    display: 'flex',
    flexDirection: 'column',
  },
  
  userName: {
    fontSize: '16px',
    fontWeight: '600',
    marginBottom: '4px',
  },
  
  userEmail: {
    fontSize: '14px',
    color: '#aaa',
  },
  
  userActions: {
    marginLeft: 'auto',
    display: 'flex',
    gap: '10px',
  },
  
  changePasswordBtn: {
    background: 'transparent',
    color: '#4a9eff',
    border: '1px solid #4a9eff',
    borderRadius: '6px',
    padding: '8px 16px',
    fontSize: '14px',
    cursor: 'pointer',
  },
  
  logoutBtn: {
    background: '#d73a49',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 16px',
    fontSize: '14px',
    cursor: 'pointer',
  },
  
  contentSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  
  description: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  
  descriptionParagraph: {
    fontSize: '14px',
    lineHeight: '1.6',
    color: '#ccc',
    margin: 0,
  },
  
  link: {
    color: '#4a9eff',
    textDecoration: 'none',
  },
  
  companyInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  
  companyLine: {
    fontSize: '12px',
    color: '#999',
    margin: 0,
  },
  
  footer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 'auto',
  },
  
  disclaimer: {
    fontSize: '12px',
    color: '#666',
    fontStyle: 'italic',
  },
  
  loading: {
    color: '#fff',
    textAlign: 'center',
    padding: '20px',
  },
  smallActionButton: {
    padding: '6px 12px',
    borderRadius: '8px',
    border: '1px solid #2E3749',
    background: '#2E4060',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '13px',
  },
  importModal: {
    width: '380px',
    maxWidth: 'calc(100vw - 40px)',
    padding: '18px',
    borderRadius: '4px',
    background: '#171D2C',
    border: '1px solid #222C3E',
    boxShadow: '0 18px 50px rgba(0, 0, 0, 0.36)',
  },

  importModalBody: {
    display: 'grid',
    gap: '20px',
  },

  importDropzone: {
    height: '124px',
    border: '1px dashed #344158',
    borderRadius: '3px',
    background: '#2B3548',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    cursor: 'pointer',
    color: '#FFFFFF',
    boxSizing: 'border-box',
  },

  importDropzoneIcon: {
    width: '13px',
    height: '13px',
    display: 'inline-block',
    borderRadius: '50%',
    border: '3px solid rgba(154, 165, 184, 0.34)',
    borderTopColor: '#98A4B8',
    boxSizing: 'border-box',
  },

  importDropzoneText: {
    color: '#FFFFFF',
    fontSize: '13px',
    fontWeight: 600,
  },

  importFormGrid: {
    display: 'grid',
    gap: '18px',
  },

  importRow: {
    display: 'grid',
    gridTemplateColumns: '112px 1fr',
    alignItems: 'center',
    gap: '18px',
  },

  importLabel: {
    color: '#8F9BB2',
    fontSize: '12px',
    fontWeight: 600,
  },

  importRadioGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '22px',
  },

  importRadioLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#FFFFFF',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    accentColor: '#3F82D8',
  },

  importInput: {
    width: '100%',
    height: '30px',
    padding: '0 10px',
    background: '#2B3548',
    border: '1px solid #3A465C',
    borderRadius: '3px',
    color: '#FFFFFF',
    fontSize: '13px',
    outline: 'none',
    boxSizing: 'border-box',
  },

  importError: {
    padding: '8px 10px',
    borderRadius: '3px',
    background: 'rgba(239, 68, 68, 0.12)',
    border: '1px solid rgba(239, 68, 68, 0.35)',
    color: '#ff9f9f',
    fontSize: '12px',
    lineHeight: 1.35,
  },

  importFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '6px',
    marginTop: '6px',
  },

  importCancelButton: {
    height: '28px',
    minWidth: '82px',
    padding: '0 14px',
    border: 'none',
    borderRadius: '2px',
    background: '#2A3344',
    color: '#FFFFFF',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 600,
  },

  importSubmitButton: {
    height: '28px',
    minWidth: '130px',
    padding: '0 14px',
    border: 'none',
    borderRadius: '2px',
    background: '#1E4F92',
    color: '#FFFFFF',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 600,
  },

  importSubmitButtonDisabled: {
    opacity: 0.48,
    cursor: 'not-allowed',
  },

  uploadDropzone: {
    padding: '30px',
    border: '2px dashed #4a6b93',
    borderRadius: '12px',
    background: '#1B2333',
    color: '#cfd3dc',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '120px',
  },
  modalInfoRow: {
    color: '#fff',
    fontSize: '14px',
    padding: '8px 0',
    borderBottom: '1px solid #2E3749',
  },
  matchSuggestion: {
    padding: '12px',
    borderRadius: '10px',
    background: '#232d42',
    border: '1px solid #2E3749',
  },
};